export type FlowVoiceConfig = {
  voiceType: 'basic' | 'premium';
  voiceId: string;
  voiceName: string;
  language?: string;
  modelId?: string;
  apiKeyRef?: string;
};

export type LanguageEntry = {
  code: string;
  label: string;
  voiceConfig?: FlowVoiceConfig;
};

export type LanguageConfig = {
  languages: LanguageEntry[];
  defaultLanguage: string;
};

export type TeXMLElement = {
  tag: string;
  attributes?: Record<string, string | undefined>;
  content?: string;
  children?: TeXMLElement[];
};

export type TeXMLContext = {
  flowId: string;
  nodeId: string;
  callSid: string;
  variables: Record<string, string>;
  speechFormats?: Record<string, string>;
  baseCallbackUrl: string;
  voiceConfig?: FlowVoiceConfig;
  elevenLabsApiKeyRef?: string;
  phoneNumber?: string;
  languageConfig?: LanguageConfig;
};

export type CallbackResult = {
  nextHandleId?: string;
  elements?: TeXMLElement[];
  variables?: Record<string, string>;
};

export type TelnyxEvent = {
  Digits?: string;
  SpeechResult?: string;
  CallSid?: string;
  From?: string;
  To?: string;
  CallStatus?: string;
  DialCallStatus?: string;
  DialCallSid?: string;
  SipHangupCause?: string;
  HangupCause?: string;
  HangupSource?: string;
  RecordingUrl?: string;
  RecordingDuration?: string;
  [key: string]: string | undefined;
};

export type ValidationResult = {
  valid: boolean;
  errors: string[];
  warnings?: string[];
};

export type VariableOutputDefinition =
  | { configKey: string; description: string; type: 'string'; availableOnHandleTypes?: string[] }
  | { name: string; description: string; type: 'string'; availableOnHandleTypes?: string[] };

export const widgetTiers = ['system', 'standard', 'premium', 'custom'] as const;
export type WidgetTier = (typeof widgetTiers)[number];

export const widgetCategories = ['voice', 'input', 'routing', 'integration', 'control'] as const;
export type WidgetCategory = (typeof widgetCategories)[number];

export function buildCallbackUrl(context: TeXMLContext): string {
  const base = `${context.baseCallbackUrl}/flows/${context.flowId}/nodes/${context.nodeId}?callSid=${context.callSid}`;
  return context.phoneNumber ? `${base}&pn=${encodeURIComponent(context.phoneNumber)}` : base;
}

export type WidgetCoreDefinition<TConfig = Record<string, unknown>> = {
  id: string;
  version: number;
  name: string;
  description: string;
  category: WidgetCategory;
  tier: WidgetTier;

  defaultConfig: TConfig;

  variables?: {
    outputs?: VariableOutputDefinition[];
  };

  texml: {
    generate: (config: TConfig, context: TeXMLContext) => TeXMLElement[];
    requiresCallback: boolean | ((config: TConfig) => boolean);
    resolveEdge?: (config: TConfig, variables: Record<string, string>) => string | undefined;
    resolveVariables?: (config: TConfig) => Record<string, string> | undefined;
    callbackHandler?: (event: TelnyxEvent, config: TConfig, variables: Record<string, string>) => CallbackResult;
  };

  validation: {
    validateConfig: (config: TConfig) => ValidationResult;
  };
};
